/** The writing rail's ink state — the OneNote-style layer over Excalidraw.
 *
 * Excalidraw owns the underlying knobs (stroke colour, width, opacity) and its
 * own properties panel edits them freely; what it lacks is a writer's rail: a
 * pen and a highlighter that each REMEMBER their own ink, a width ladder finer
 * than its three presets, and a few quick colours always within thumb reach.
 * This module is the pure, framework-free state behind that rail — persisted to
 * localStorage, so a pen tuned in one document is the same pen in the next.
 *
 * Everything here is storage-shaped, never resolved: an adaptive colour is
 * stored as {@link ADAPTIVE_INK} and resolved against the PAPER at render time,
 * so the same pen writes near-black on a white sheet and near-white on a dark
 * one instead of vanishing into it.
 */

export type InkTool = "pen" | "highlighter";

/** Sentinel for paper-adaptive ink. Stored rather than resolved so the pen keeps
 * following the paper it is used on — resolving at save time would freeze white
 * ink into a note that later turns light (and vice versa). */
export const ADAPTIVE_INK = "adaptive";

/** What {@link ADAPTIVE_INK} resolves to. Not pure #000/#fff: Excalidraw's own
 * ink is #1e1e1e, and a hair off-white reads as chalk rather than glare. */
export const INK_ON_LIGHT = "#1e1e1e";
export const INK_ON_DARK = "#f4f4f5";

/** A stored colour → the colour to actually draw with on this paper. */
export function resolveInk(color: string, paper: "light" | "dark"): string {
  if (color !== ADAPTIVE_INK) return color;
  return paper === "dark" ? INK_ON_DARK : INK_ON_LIGHT;
}

/** Excalidraw renders a freedraw stroke at roughly `strokeWidth * 4.25` px (its
 * perfect-freehand `size`). The rail previews widths at their REAL on-paper
 * thickness, so it multiplies by this — the same constant the server-side
 * flatten uses (services/drawing_convert.py FREEDRAW_SIZE_FACTOR). */
export const FREEDRAW_SIZE_FACTOR = 4.25;

/** The pen's width ladder in Excalidraw strokeWidth units — hairline (≈1.5 px
 * on paper) to marker (≈21 px). Excalidraw's own picker offers three; writing
 * by hand needs the fine end of the range that a diagramming tool doesn't. */
export const PEN_WIDTHS = [0.35, 0.6, 1, 1.75, 3, 5];

/** The highlighter is a broad translucent nib — a different, chunkier ladder. */
export const HIGHLIGHTER_WIDTHS = [3, 5, 8, 12, 18, 26];

export function widthsFor(tool: InkTool): number[] {
  return tool === "highlighter" ? HIGHLIGHTER_WIDTHS : PEN_WIDTHS;
}

/** A highlighter IS a wide translucent pen — the opacity is what makes it lay
 * under text rather than over it. */
export const HIGHLIGHTER_OPACITY = 40;
export const PEN_OPACITY = 100;

export function opacityFor(tool: InkTool): number {
  return tool === "highlighter" ? HIGHLIGHTER_OPACITY : PEN_OPACITY;
}

/** One tool's remembered ink. Colour is a STORED value (possibly the adaptive
 * sentinel); width is an Excalidraw strokeWidth; opacity is 0..100. Opacity is
 * remembered per tool rather than derived, so tuning a highlighter down in
 * Excalidraw's own panel sticks instead of snapping back on the next pick. */
export interface InkSetting {
  color: string;
  width: number;
  opacity: number;
}

/** The rail's three quick-colour slots. Slot 0 defaults to adaptive ink — the
 * "just write" pen that is black on paper and white on a dark page. */
export const DEFAULT_COLOR_SLOTS: string[] = [ADAPTIVE_INK, "#1971c2", "#e03131"];

export const DEFAULT_INK: Record<InkTool, InkSetting> = {
  pen: { color: ADAPTIVE_INK, width: 1, opacity: PEN_OPACITY },
  highlighter: { color: "#ffd43b", width: 12, opacity: HIGHLIGHTER_OPACITY },
};

const KEY = "graphmind.drawing.ink";

interface Persisted {
  slots?: unknown;
  ink?: unknown;
}

function storage(): Storage | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch {
    return null;
  }
}

function read(): Persisted {
  const s = storage();
  if (!s) return {};
  try {
    const raw = s.getItem(KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? (parsed as Persisted) : {};
  } catch {
    return {};
  }
}

function write(next: Persisted): void {
  const s = storage();
  if (!s) return;
  try {
    s.setItem(KEY, JSON.stringify({ ...read(), ...next }));
  } catch {
    /* quota / disabled — the rail still works, it just won't remember */
  }
}

/** A stored colour is either the adaptive sentinel or a #rrggbb / #rgb string.
 * Anything else came from a corrupt/hand-edited store and is refused. */
export function isStorableColor(value: unknown): value is string {
  return (
    value === ADAPTIVE_INK ||
    (typeof value === "string" && /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(value))
  );
}

/** The three quick-colour slots (defaults where storage is missing or junk). */
export function loadColorSlots(): string[] {
  const saved = read().slots;
  if (!Array.isArray(saved)) return [...DEFAULT_COLOR_SLOTS];
  return DEFAULT_COLOR_SLOTS.map((fallback, i) =>
    isStorableColor(saved[i]) ? (saved[i] as string) : fallback,
  );
}

export function saveColorSlots(slots: string[]): void {
  write({ slots: slots.filter(isStorableColor).slice(0, DEFAULT_COLOR_SLOTS.length) });
}

function sanitizeInk(value: unknown, fallback: InkSetting, allowed: number[]): InkSetting {
  if (!value || typeof value !== "object") return fallback;
  const v = value as Partial<InkSetting>;
  // The width must be a real positive number, but NOT necessarily one of the
  // ladder values — Excalidraw's own panel can set anything, and the rail keeps
  // whatever the user last drew with. `allowed` only seeds the fallback.
  const width =
    typeof v.width === "number" && Number.isFinite(v.width) && v.width > 0
      ? v.width
      : (allowed[0] ?? fallback.width);
  const opacity =
    typeof v.opacity === "number" && Number.isFinite(v.opacity) && v.opacity >= 0 && v.opacity <= 100
      ? v.opacity
      : fallback.opacity;
  return { color: isStorableColor(v.color) ? v.color : fallback.color, width, opacity };
}

/** Each tool's remembered ink. */
export function loadInk(): Record<InkTool, InkSetting> {
  const saved = read().ink as Partial<Record<InkTool, unknown>> | undefined;
  return {
    pen: sanitizeInk(saved?.pen, DEFAULT_INK.pen, PEN_WIDTHS),
    highlighter: sanitizeInk(saved?.highlighter, DEFAULT_INK.highlighter, HIGHLIGHTER_WIDTHS),
  };
}

export function saveInk(ink: Record<InkTool, InkSetting>): void {
  write({ ink });
}

/** The on-screen diameter (px) to preview `width` at, clamped so a hairline is
 * still visible and a marker still fits its button. */
export function previewDotPx(width: number): number {
  return Math.max(2, Math.min(26, width * FREEDRAW_SIZE_FACTOR));
}
